<template lang="html">
  <transition
    v-if="isFilterOpen"
    name="filter"
  >
    <aside
      v-tracking-context="{ content_element: 'filter_settings' }"
      class="filter-settings"
    >
      <div class="filter-settings__headline flex-container align-justify">
        {{ 'client.filter_settings.headline' | t }}
        <a
          v-click-track="{ action_type: 'filter_close' }"
          class="filter-settings__close"
          @click.prevent="filterClose"
        >
          <svg-icon icon-name="close" />
        </a>
      </div>
      <div class="filter-settings__contents">
        <div
          v-for="(category, idx) in categories"
          :key="category.key"
          v-tracking-context="{ event_element: 'category', event_element_pos: idx + 1 }"
          class="filter-settings__category"
          :class="{'filter-settings__category--active': visibleCategory === category.key}"
        >
          <div
            v-click-track="{ action_type: 'category_toggle', target: `#${category.key}` }"
            class="filter-settings__category-label"
            :class="{'filter-settings__label--applied': partiallyCheckedCollections.has(`${category.id}`)}"
            @click="toggleCategory(category.key)"
          >
            <div
              class="filter-settings__label"
            >
              {{ category.label }}
            </div>
            <svg-icon
              icon-name="chevron-left"
              class-name="filter-settings__chevron"
              style="width: 24px"
            />
          </div>
          <div class="filter-settings__category-body">
            <div
              v-for="group in category.grouped_category_tags"
              :key="group.key"
              class="filter-settings__group"
              :class="{'filter-settings__group--active': visibleGroup === group.key || !group.key}"
            >
              <div
                v-if="group.key"
                v-click-track="{ action_type: 'category_group_toggle', target: `#${group.key}` }"
                class="filter-settings__group-label"
                @click="toggleGroup(group.key)"
              >
                <div
                  class="filter-settings__label"
                  :class="{'filter-settings__label--applied': partiallyCheckedCollections.has(`${category.id}-${group.id}`)}"
                >
                  {{ group.label }}
                </div>
                <svg-icon
                  icon-name="chevron-left"
                  class-name="filter-settings__chevron"
                  style="width: 24px"
                />
              </div>
              <div class="filter-settings__group-body">
                <div class="filter-settings__tag">
                  <input
                    :id="`filter-${category.id}-${group.id}-all`"
                    v-click-track="{ action_type: 'category_tag_toggle', target: `#${group.key}-all` }"
                    class="custom-input custom-input--checkbox"
                    type="checkbox"
                    :checked="checkedGroups[`${category.id}-${group.id}`]"
                    @click="toggleGroupTags(category, group)"
                  >
                  <label
                    class="filter-settings__tag-label"
                    :for="`filter-${category.id}-${group.id}-all`"
                  >{{ 'client.filter_settings.select_all' | t }}</label>
                </div>
                <div
                  v-for="tag in group.group_tags"
                  :key="tag.key"
                  class="filter-settings__tag"
                >
                  <input
                    :id="`filter-${category.id}-${group.id}-${tag.id}`"
                    v-click-track="{ action_type: 'category_tag_toggle', target: `#${tag.key}` }"
                    class="custom-input custom-input--checkbox"
                    type="checkbox"
                    :name="tag.key"
                    :checked="checkedTagsMap[tag.key]"
                    @click="toggleTag(tag.key)"
                  >
                  <label
                    class="filter-settings__tag-label"
                    :for="`filter-${category.id}-${group.id}-${tag.id}`"
                  >{{ tag.label }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="filter-settings__button">
        <a
          v-click-track="{ action_type: 'results_show' }"
          class="button button--primary"
          :class="{'disabled': resultsLoading}"
          @click.prevent="showResults"
        >
          <span v-if="!resultsLoading">{{ 'client.filter_settings.show_results' | t({ count: resultsCount }) }}</span>
          <span v-else>...</span>
        </a>
      </div>
    </aside>
  </transition>
</template>

<script>
import jump from 'jump.js-cancelable'
import { flatMap, flatMapDeep, fromPairs, every, some } from 'lodash-es'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

export default {
  data: () => ({
    visibleCategory: null,
    visibleGroup: null
  }),
  computed: {
    ...mapGetters(['checkedTagsMap']),
    ...mapState(['categories', 'searchProfile', 'isFilterOpen', 'resultsCount', 'resultsLoading']),
    checkedGroups () {
      return fromPairs(flatMap(this.categories, category => {
        return category.grouped_category_tags.filter(group => every(group.group_tags, tag => this.checkedTagsMap[tag.key]))
          .map(group => [`${category.id}-${group.id}`, true])
      }))
    },
    partiallyCheckedCollections () {
      return new Set(flatMapDeep(this.categories, category => {
        return category.grouped_category_tags.filter(group => some(group.group_tags, tag => this.checkedTagsMap[tag.key]))
          .map(group => [`${category.id}`, `${category.id}-${group.id}`])
      }))
    }
  },
  mounted () {
    this.setupLegacy()
  },
  methods: {
    ...mapActions(['setupLegacy']),
    ...mapMutations(['filterClose', 'searchProfileAddTag', 'searchProfileRemoveTag', 'searchProfileAddTags', 'searchProfileRemoveTags']),
    showResults () {
      this.filterClose()
      this.$nextTick(() => {
        jump.jump('.result__list-wrapper', {
          offset: -24,
          duration: distance => Math.max(Math.abs(distance), 300)
        })
      })
    },
    toggleTag (tagKey) {
      if (this.checkedTagsMap[tagKey]) {
        this.searchProfileRemoveTag(tagKey)
      } else {
        this.searchProfileAddTag(tagKey)
      }
    },
    toggleCategory (key) {
      if (this.visibleCategory === key) {
        this.visibleCategory = null
      } else {
        this.visibleCategory = key
      }
    },
    toggleGroup (key) {
      if (this.visibleGroup === key) {
        this.visibleGroup = null
      } else {
        this.visibleGroup = key
      }
    },
    toggleGroupTags (category, group) {
      if (this.checkedGroups[`${category.id}-${group.id}`]) {
        this.searchProfileRemoveTags(group.group_tags.map(tag => tag.key))
      } else {
        this.searchProfileAddTags(group.group_tags.map(tag => tag.key))
      }
    }
  }
}
</script>

<style lang='scss' scoped>
.filter-settings {
  transition-duration: 0.3s;
}

.filter-enter,
.filter-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
